import { useCallback, useEffect, useState } from 'react'

export type ThemePreference = 'light' | 'dark' | 'system'
export type ResolvedTheme = 'light' | 'dark'

const KEY = 'theme'

function storedPreference(): ThemePreference | null {
  try {
    const value = localStorage.getItem(KEY)
    return value === 'light' || value === 'dark' || value === 'system' ? value : null
  } catch {
    // Storage can be unavailable. The system preference still applies, so
    // there is nothing to report.
    return null
  }
}

function systemPrefersDark(): boolean {
  return typeof matchMedia === 'function' && matchMedia('(prefers-color-scheme: dark)').matches
}

function resolve(preference: ThemePreference): ResolvedTheme {
  return preference === 'system' ? (systemPrefersDark() ? 'dark' : 'light') : preference
}

/**
 * Light, Dark, or System, with `resolved` carrying which of the two the
 * page is actually painted in. System is the default: nothing chosen yet
 * means the operating system governs, and a chosen preference is stored
 * and applied as `data-theme` on the document element, which the
 * stylesheet reads. Choosing System again clears the stored choice rather
 * than storing the word, so a later OS change keeps taking effect without
 * needing to be re-chosen.
 */
export function useTheme() {
  const [preference, setPreference] = useState<ThemePreference>(() => storedPreference() ?? 'system')
  const [resolved, setResolved] = useState<ResolvedTheme>(() => resolve(preference))

  useEffect(() => {
    const root = document.documentElement
    if (preference === 'system') {
      delete root.dataset.theme
    } else {
      root.dataset.theme = preference
    }
  }, [preference])

  useEffect(() => {
    if (typeof matchMedia !== 'function') return
    const query = matchMedia('(prefers-color-scheme: dark)')
    const onChange = () => {
      if (preference === 'system') setResolved(systemPrefersDark() ? 'dark' : 'light')
    }
    query.addEventListener('change', onChange)
    return () => query.removeEventListener('change', onChange)
  }, [preference])

  const choose = useCallback((next: ThemePreference) => {
    setPreference(next)
    setResolved(resolve(next))
    try {
      if (next === 'system') {
        localStorage.removeItem(KEY)
      } else {
        localStorage.setItem(KEY, next)
      }
    } catch {
      // A preference that cannot be remembered still applies for this visit.
    }
  }, [])

  return { preference, resolved, choose }
}
